import { useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import firebase from 'src/utils/firebase';
import { buildApiUrl } from 'src/utils/api';

const timezones = [
  'America/St_Johns', 'America/Halifax', 'America/Toronto', 'America/Winnipeg',
  'America/Edmonton', 'America/Vancouver', 'America/Los_Angeles', 'America/Denver',
  'America/Chicago', 'America/New_York'
];

const AccountProvisioning = () => {
  const auth = useAuth();
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [form, setForm] = useState({
    shopName: '',
    contactName: auth.user?.name || '',
    timezone: timezones.includes(detected) ? detected : 'America/Toronto',
    termsAccepted: false,
    privacyAccepted: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setFieldErrors({});
    try {
      const token = await firebase.auth().currentUser.getIdToken();
      await axios.post(buildApiUrl('/api/auth/provision'), form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await auth.refreshAccountContext();
      window.location.assign('/app/applications/workflow');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError.message || 'Unable to finish account setup.');
      setFieldErrors(requestError?.response?.data?.fieldErrors || {});
      setSaving(false);
    }
  };

  return (
    <Box minHeight="100vh" py={{ xs: 3, md: 8 }} bgcolor="background.default">
      <Container maxWidth="sm">
        <Stack component="form" onSubmit={submit} spacing={2} bgcolor="background.paper" p={{ xs: 2, sm: 4 }}>
          <Typography variant="h3">Complete your shop setup</Typography>
          <Typography color="text.secondary">Create the shop workspace linked to your signed-in account.</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Business name" value={form.shopName}
required autoFocus
            onChange={(event) => update('shopName', event.target.value)}
            error={Boolean(fieldErrors.shopName)} helperText={fieldErrors.shopName}
          />
          <TextField
            label="Your name" value={form.contactName}
autoComplete="name"
            onChange={(event) => update('contactName', event.target.value)}
          />
          <TextField
            select label="Business timezone"
value={form.timezone}
            onChange={(event) => update('timezone', event.target.value)}
            error={Boolean(fieldErrors.timezone)} helperText={fieldErrors.timezone}
          >
            {timezones.map((timezone) => <MenuItem key={timezone} value={timezone}>{timezone.replace('_', ' ')}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Checkbox checked={form.termsAccepted} onChange={(event) => update('termsAccepted', event.target.checked)} />}
            label={<>I accept the <Link component={RouterLink} target="_blank" to="/terms">Terms of Service</Link></>}
          />
          {fieldErrors.termsAccepted && <Typography color="error" variant="caption">{fieldErrors.termsAccepted}</Typography>}
          <FormControlLabel
            control={<Checkbox checked={form.privacyAccepted} onChange={(event) => update('privacyAccepted', event.target.checked)} />}
            label={<>I accept the <Link component={RouterLink} target="_blank" to="/privacy">Privacy Policy</Link></>}
          />
          {fieldErrors.privacyAccepted && <Typography color="error" variant="caption">{fieldErrors.privacyAccepted}</Typography>}
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Creating shop...' : 'Finish setup'}
          </Button>
          <Button type="button" color="inherit" onClick={auth.logout}>Sign out</Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default AccountProvisioning;
